/**
 * Site institucional da HT CAPITAL — landing, modelo, fases e sobre.
 *
 * As quatro páginas vieram do Dashboard sem reescrita (ver .ai-memory). O que NÃO veio junto foi
 * de propósito: o processo seletivo continua no app, porque cria usuário e depende de sessão.
 * Aqui não há nada autenticado.
 */
import { useEffect } from "react";
import { Route, Switch, Redirect, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LandingPage from "@/pages/landing-page";
import AboutPage from "@/pages/about-page";
import ModelPage from "@/pages/model-page";
import PhasesPage from "@/pages/phases-page";
import { API_BASE_URL, APP_BASE_URL } from "@/lib/config";

/**
 * A landing lê `/api/site-settings/public` para textos editáveis pelo backoffice. É a ÚNICA
 * chamada de rede do site, e é pública — sem cookie, sem credenciais, nada cross-site.
 * Se ela falhar, a página cai nos textos padrão em vez de quebrar.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey }) => {
        const resposta = await fetch(API_BASE_URL + String(queryKey[0]));
        if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
        return resposta.json();
      },
      retry: 1,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Mesmo formato de `Dashboard/shared/tenant-slugs.ts` (`TENANT_SLUG_PATTERN`). Duplicado de
 * propósito: este site é deploy separado, sem acesso a `@shared/*` — se um mudar, mudar o outro.
 */
const TENANT_SLUG_PATTERN = /^[a-z0-9-]{2,64}$/;

/** Rotas que existem AQUI. Qualquer outro primeiro segmento é, por eliminação, link de mesa. */
const ROTAS_DO_SITE = new Set(["", "sobre", "modelo", "fases"]);

/**
 * Link público de mesa branca por caminho (`htcapital.com.br/<mesa>/processo-seletivo`) é
 * resolvido pelo APP, não por aqui — o roteamento de tenant mora lá (`resolveEffectiveTenant`).
 *
 * Sem isto, esses links cairiam no fallback de SPA e mostrariam a landing da HT Capital. Foi
 * exatamente o que aconteceu com o hts-site em 2026-09-06, o dono testando link de mesa: o
 * domínio de marketing engolia o caminho e servia a página errada. Aqui o risco é maior ainda,
 * porque o apex `htcapital.com.br` HOJE aponta pro App Service e esses links funcionam.
 */
function usarRedirecionamentoDeMesa(): boolean {
  const [caminho] = useLocation();
  const limpo = caminho.replace(/\/+$/, "");
  const primeiro = (limpo.split("/").filter(Boolean)[0] || "").toLowerCase();
  const ehLinkDeMesa = !ROTAS_DO_SITE.has(primeiro) && TENANT_SLUG_PATTERN.test(primeiro);

  useEffect(() => {
    if (!ehLinkDeMesa) return;
    window.location.replace(APP_BASE_URL + window.location.pathname + window.location.search);
  }, [ehLinkDeMesa]);

  return ehLinkDeMesa;
}

export default function App() {
  const redirecionando = usarRedirecionamentoDeMesa();

  // Branco de propósito enquanto o `replace` acontece — evita piscar a landing errada no caminho.
  if (redirecionando) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        <Route path="/" component={LandingPage} />
        <Route path="/sobre" component={AboutPage} />
        <Route path="/modelo" component={ModelPage} />
        <Route path="/fases" component={PhasesPage} />
        {/* Qualquer outra coisa era rota do app antes da separação — devolve pra landing
            em vez de mostrar 404 pra quem seguiu link antigo. */}
        <Route><Redirect to="/" /></Route>
      </Switch>
    </QueryClientProvider>
  );
}
